import { Grid } from '@mantine/core';
import { useMemo } from 'react';
import type { MantineColumn } from '../../../../../../react-commons/components/table/MantineTable';
import { useNestedDialogFunction } from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    defaultSystemUserGroupRoleAssignmentResource,
    type SystemUserGroupRoleAssignmentResource,
    useSystemUserGroupRoleAssignmentActions,
} from '../../../../resource/resources/persistables/user/systemUserGroupRoleAssignmentResource';
import { useSystemUserRoleActions } from '../../../../resource/resources/persistables/user/systemUserRole';
import { CrudValidationTable } from '../../../reusable-components/tables/CrudValidationTable';
import { AddOrUpdateSystemUserGroupRoleAssignmentDialog } from './AddOrUpdateSystemUserGroupRoleAssignmentDialog';
import { OverviewCrudValidationTable } from '../../../reusable-components/tables/OverviewCrudValidationTable';

export const SystemUserGroupRolesTabContent = ({
                                                   id,
                                                   systemUserGroupRoleAssignment,
                                               }: {
    id: string | undefined;
    systemUserGroupRoleAssignment: SystemUserGroupRoleAssignmentResource | undefined;
}) => {
    const nestedDialog = useNestedDialogFunction();

    const systemUserRoleActions = useSystemUserRoleActions();
    const systemUserGroupRoleAssignmentActions = useSystemUserGroupRoleAssignmentActions();

    const roles = systemUserRoleActions.useGetAllResources();
    const updateRoleAssignment = systemUserGroupRoleAssignmentActions.useUpdateResource();

    const openAddOrUpdateSystemUserGroupRoleAssignmentDialog = () => {
        if (roles.data) {
            nestedDialog.showNestedDialog(
                <AddOrUpdateSystemUserGroupRoleAssignmentDialog
                    roles={roles.data}
                    systemUserRoleAssignment={
                        systemUserGroupRoleAssignment ?? {
                            ...defaultSystemUserGroupRoleAssignmentResource,
                            systemUserGroup: id ?? defaultSystemUserGroupRoleAssignmentResource.systemUserGroup,
                        }
                    }
                    onClose={nestedDialog.closeNestedDialog}
                />
            );
        }
    };

    const columns = useMemo((): Array<MantineColumn<{ roleNameId: string }>> => {
        return [
            {
                type: 'reference',
                header: 'Role',
                Header: null,
                accessorKey: 'roleNameId',
                Cell: ({ row }) => (
                    <ControlledInputs.String
                        nullable={false}
                        readOnly={true}
                        value={roles.data?.find((value) => value.id === row.original.roleNameId)?.name ?? ''}
                        style={{
                            paddingLeft: 0,
                            width: '100%',
                            borderBottom: 'none',
                        }}
                    />
                ),
            },
        ];
    }, [roles.data]);

    return (
        <Grid>
            <Grid.Col span={12}>
                <OverviewCrudValidationTable
                    columns={columns}
                    data={systemUserGroupRoleAssignment?.systemUserRoles.map((roleNameId) => ({ roleNameId }))}
                    resourceType={ResourceType.SYSTEM_USER_GROUP_ROLE_ASSIGNMENT}
                    label={'User Group Roles Assigned'}
                    onAdd={openAddOrUpdateSystemUserGroupRoleAssignmentDialog}
                    onDelete={(resource) => {
                        if (systemUserGroupRoleAssignment) {
                            const newState = { ...systemUserGroupRoleAssignment };
                            newState.systemUserRoles = newState.systemUserRoles.filter(
                                (value) => value !== resource.roleNameId
                            );
                            updateRoleAssignment.mutate({
                                resource: newState,
                                id: newState.id,
                            });
                        }
                    }}
                    onEdit={undefined}
                    validationData={undefined}
                />
            </Grid.Col>
        </Grid>
    );
};
